import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';

import * as Api from '@Api/index';
import * as Configurator from '@Stores/Configurator';
import * as PageProductStore from '@Stores/PageProduct';
import useDeps from '@Contexts/DI/useDeps';
import useNavigation from '@Navigation/useNavigation';
import useSearchParams from '@Hooks/useSearchParams';
import ApiProduct from '@Api/Product';
import useKeys from './useKeys';
import queryStringToObject from './helpers/queryStringToObject';
import filterInStock from './helpers/filterInStock';
import useInStock from './useInStock';

import type { ConfiguratorData } from '@Pages/PageProduct';
import type { SelectedOptions } from '@Api/Product/getConfigurator';
import type { FilterResultData } from '@Api/Configurator/getFilterResult';

export function getConfiguratorKeys(translite: string, selectedPv: SelectedOptions) {
  const { language, region } = Api.getRequest();
  const addKeys = Object.values(selectedPv);
  return ['configurator-on-page', { region, lang: language.id, translite, addKeys }];
}

export const useChangeConfiguration = () => {
  const page = PageProductStore.useData();
  const { id } = page.product;
  const { logger } = useDeps();
  const searchParams = useSearchParams();
  const queryClient = useQueryClient();
  const selectedOptions = Configurator.useSelectedParamsObject();
  const oldHash = searchParams.get('hash');
  const { navigate } = useNavigation();
  const inStock = useInStock();
  const abortController = useRef<AbortController>(null);
  const { configurator: queryKey, filters: filterKeys } = useKeys();

  const { mutateAsync: changeConfiguration, isPending } = useMutation<ConfiguratorData, Error>({
    mutationKey: queryKey,
    mutationFn: async () => {
      const response = await ApiProduct.getConfigurator(
        { shopProductId: id, selectedOptions },
        { signal: abortController.current?.signal },
      );

      const images = page.gallery.map((image, index) =>
        index === 0 && response.configuration?.image
          ? { ...image, src: response.configuration.image }
          : image,
      );

      const configuration = response.configuration ? { ...response.configuration, images } : null;

      const filters = queryClient.getQueryData<FilterResultData>(filterKeys);

      return {
        ...response,
        tabs: filterInStock({ tabs: response.tabs, inStock, filters }),
        configuration,
      };
    },
    onSuccess: async (data) => {
      if (!data?.configuration?.url) return;

      const url = `${data.configuration.url}${oldHash ? `&hash=${oldHash}` : ''}`;
      const translite = url.split('/product/')[1].split('?')[0];
      const newSearchParams = new URLSearchParams(url.split('/product/')[1].split('?')[1]);

      const newKeys = getConfiguratorKeys(
        translite,
        queryStringToObject(newSearchParams.toString()),
      );

      await queryClient.setQueryData(newKeys, data);
      navigate(data.configuration.url, { preventScrollReset: true, replace: true });
    },
    onError: (error) => {
      logger.error(error);
    },
  });

  const handleChangeConfiguration = async () => {
    if (isPending) {
      abortController.current?.abort('abort previous request');
    }

    abortController.current = new AbortController();
    await changeConfiguration();
  };

  return {
    changeConfiguration: handleChangeConfiguration,
    isPending,
  };
};

export default useChangeConfiguration;
